import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoopyAvatar } from '../loopy';
import './DictationGuidancePanel.css';

export interface GuidanceItem {
    type: 'phonics' | 'prosody' | 'morphology' | 'sight_word' | 'spelling' | 'collocation';
    categoryKey: string;
    word: string;
    expected: string;
    userInput: string;
    tipKey: string;
    tipNamespace: string;
    learnMorePath: string;
    confidence: number;
    tipVars?: Record<string, string>;
}

interface DictationGuidancePanelProps {
    items: GuidanceItem[];
}

const TypeIcon: React.FC<{ type: GuidanceItem['type'] }> = ({ type }) => {
    switch (type) {
        case 'phonics':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 7V4h16v3" /><path d="M9 20h6" /><path d="M12 4v16" />
                </svg>
            );
        case 'prosody':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 10v3" /><path d="M6 6v11" /><path d="M10 3v18" /><path d="M14 8v7" /><path d="M18 5v13" /><path d="M22 10v3" />
                </svg>
            );
        case 'morphology':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
            );
        case 'spelling':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
            );
        case 'sight_word':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
                </svg>
            );
        case 'collocation':
            return (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
            );
    }
};

const DictationGuidancePanel: React.FC<DictationGuidancePanelProps> = ({ items }) => {
    const { t } = useTranslation(['guidanceRef', 'phonicsRef', 'prosodyRef', 'morphRef', 'sightWordsRef']);
    const [collapsed, setCollapsed] = useState(false);

    if (!items || items.length === 0) return null;

    return (
        <div className="dgp">
            <button
                className="dgp__header"
                onClick={() => setCollapsed(prev => !prev)}
                type="button"
                aria-expanded={!collapsed}
            >
                <LoopyAvatar size="xs" state="hint" emotion="magnifier" ariaLabel={t('title', { ns: 'guidanceRef' })} />
                <span className="dgp__title">
                    {t('title', { ns: 'guidanceRef' })}
                </span>
                <svg
                    className={`dgp__chevron ${collapsed ? 'dgp__chevron--collapsed' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>

            {!collapsed && (
                <div className="dgp__body">
                    {items.map((item, index) => {
                        const tip = t(item.tipKey, {
                            ns: item.tipNamespace,
                            expected: item.expected,
                            typed: item.userInput,
                            word: item.word,
                            defaultValue: '',
                            ...item.tipVars,
                        });

                        return (
                            <div key={`${item.categoryKey}-${index}`} className={`dgp__item dgp__item--${item.type}`}>
                                <div className="dgp__item-header">
                                    <span className={`dgp__type-badge dgp__type-badge--${item.type}`}>
                                        <TypeIcon type={item.type} />
                                        {t(`types.${item.type}`, { ns: 'guidanceRef' })}
                                    </span>
                                    <span className="dgp__word-compare">
                                        <span className="dgp__word-expected">{item.expected}</span>
                                        <svg className="dgp__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14" />
                                            <path d="M12 5l7 7-7 7" />
                                        </svg>
                                        <span className="dgp__word-user">{item.userInput}</span>
                                    </span>
                                </div>
                                {tip && <p className="dgp__tip">{tip}</p>}
                                <a
                                    className="dgp__link"
                                    href={item.learnMorePath}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t('learnMore', { ns: 'guidanceRef' })} →
                                </a>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default DictationGuidancePanel;
